<template>
  <InputWrapper
    v-slot="{ ariaProps, inputId }"
    input-id="event-related-stop-id"
    class="field"
    :label="t('common.relatedParam', { param: t('shipments.stops') })"
    optional
  >
    <RygenDropdown
      :id="inputId"
      v-model="internalValue.relatedStopId"
      v-bind="ariaProps"
      :options="shipmentStops"
      :show-clear="!isDisabled"
      :disabled="isDisabled"
      append-to="body"
      option-value="id"
      data-key="id"
      option-label="stopLabel"
      filter
      :placeholder="t('common.relatedParam', { param: t('shipments.stops') })"
    >
      <template v-if="relatedStop" #value>
        <StopLabel :stop="relatedStop" />
      </template>

      <template #option="{ option }">
        <StopLabel :stop="option" />
      </template>
    </RygenDropdown>
  </InputWrapper>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useModelValue } from '@rygen/rygen-ui/composables';

import {
  isShipmentDeliveryEvent,
  isShipmentOriginEvent,
  ShipmentEvent,
  ShipmentStop,
} from '@/model';
import { buildShipmentEvent } from '@/util';

import StopLabel from '@/components/location/StopLabel.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';

const props = defineProps({
  modelValue: {
    type: Object as PropType<ShipmentEvent>,
    required: true,
  },
  shipmentStops: {
    type: Array as PropType<ShipmentStop[]>,
    default: () => [],
  },
});
const { modelValue, shipmentStops } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', newVal: ShipmentEvent): void;
}>();

const { t } = useI18n();
const { internalValue } = useModelValue(modelValue, {
  emit,
  factoryMethod: (opts) => buildShipmentEvent(opts),
});

const isDisabled = computed(() => {
  return (
    isShipmentOriginEvent(internalValue.value.eventType) ||
    isShipmentDeliveryEvent(internalValue.value.eventType) ||
    internalValue.value.isMilestone
  );
});

const shipmentStopById = computed(() => {
  return shipmentStops.value.reduce(
    (acc, stop) => {
      if (stop.id) {
        acc[stop.id] = stop;
      }
      return acc;
    },
    {} as Record<number, ShipmentStop>,
  );
});

const relatedStop = computed(() => {
  return internalValue.value.relatedStopId
    ? shipmentStopById.value[internalValue.value.relatedStopId]
    : undefined;
});
</script>
